import { GetServerSideProps, InferGetServerSidePropsType, Redirect } from 'next'
import { MetaHead } from '../../components'
import { ProductBrandPage, Layout, ResponsiveContext } from 'components'
import {
  dehydrate,
  DehydratedState,
  QueryClient
} from '@smuchile/pkg-unimarc-hooks'
import {
  cleanPageNumber,
  cleanPageNumberParam,
  getQueryKeyToRequest
} from 'shared/helpers'
import { IIntelligenceSearch } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/IIntelligenceSearch'
import { useRouter } from 'next/router'
import { paginationCal } from 'shared/helpers/paginationCal'
import { replaceAllString } from 'shared/utils/cleanStrings/cleanString'
import { getCookiesFromStaticPages } from 'shared/helpers/getCookiesFromStaticPages'
import { NextPageWithLayout } from 'shared/interfaces/Layout'
import { getGlobalStyle } from '@smuchile/pkg-unimarc-components/helpers'
import { ICustomGetServerSidePropsResult } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/ICustomGetServerSidePropsResult'
import {
  IPostProductsSearchProps,
  postProductsSearch
} from '@smuchile/pkg-unimarc-hooks/services/postProductsSearch'
import CurtainWrapper from 'components/CurtainWrapper'

export interface BrandProps {
  error?: boolean
  dehydratedState?: DehydratedState
  intelliSearchData?: IIntelligenceSearch
  page?: number
  isMobile?: boolean
  query?: string
}

const Brand: NextPageWithLayout = ({
  query,
  page,
  testFilters,
  isMobile
}: InferGetServerSidePropsType<
  typeof getServerSideProps
>): React.ReactElement => {
  const router = useRouter()
  const brand = router?.query?.brand as Array<string>

  return (
    <ResponsiveContext isMobile={isMobile}>
      <>
        <MetaHead
          canonicalPath={cleanPageNumber(router?.asPath)}
          categories={cleanPageNumberParam(brand)}
          contentDescription={`Encuentra los mejores precios de "${brand}" en Unimarc.cl y App Unimarc`}
          titleName={brand?.[0]}
          type='BRAND'
        />
        <ProductBrandPage
          brand={brand.toString()}
          isMobile={isMobile}
          page={page}
          search={query}
          testFilters={testFilters}
        />
      </>
    </ResponsiveContext>
  )
}

Brand.getLayout = (page) => {
  return (
    <CurtainWrapper>
      <Layout backgroundColor={getGlobalStyle('--color-background-white')}>
        {page}
      </Layout>
    </CurtainWrapper>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context
): Promise<ICustomGetServerSidePropsResult<BrandProps, Redirect>> => {
  const queryClient: QueryClient = new QueryClient()
  const page = Number(context?.query?.page || 1)

  const brand: string = replaceAllString({
    str: context?.query?.brand?.[0],
    find: '-',
    replace: ' '
  })

  const { offset, end } = paginationCal({ page, limit: 50 })

  const options: IPostProductsSearchProps = {
    brands: brand && [brand],
    from: offset.toString(),
    to: end.toString()
  }
  const { newOptions } = getCookiesFromStaticPages({
    options,
    listPaths: context?.params?.brand as Array<string>
  })

  const queryKeyBrand = [
    'productsSearch',
    getQueryKeyToRequest([
      { value: offset },
      { value: end },
      { value: context?.query?.brand?.[0] }
    ])
  ]
  await queryClient.prefetchQuery(queryKeyBrand, () => {
    return postProductsSearch(newOptions)
  })

  const intelliSearchData = queryClient.getQueryData(queryKeyBrand)

  if (intelliSearchData?.['error']) {
    return {
      props: {},
      notFound: true
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      page: page || null,
      query: brand
    }
  }
}
export default Brand
